import { type PersonWithHousehold } from "@/hooks/usePeople";
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";

/**
 * Privacy settings for a person, sourced from `directory_profiles`
 * (the `person_privacy_settings` table this used to reference was dropped by
 * 20260227000326_simplify_directory_privacy.sql). A `directory_profiles` row
 * is auto-created for every person, but that no longer means opted in:
 * `directory_opt_in` here reflects the row's `is_listed` column (#4970),
 * which starts `false` and only becomes `true` once a member affirmatively
 * lists themselves. A MISSING entry in `privacyByPerson` (no row at all —
 * e.g. a company/non-individual entity) is also treated as excluded.
 *
 * Per-field defaults when a row exists but a flag is null/undefined — these
 * are intentionally NOT all "false (conservative)":
 *
 *   share_email     → defaults TRUE   (contact info is the directory's purpose)
 *   share_phone     → defaults TRUE
 *   share_address   → defaults TRUE
 *   share_birthdate → defaults FALSE  (birth dates enable identity-theft / age
 *                                      gating; opt-in only)
 *   directory_opt_in → the row's real `is_listed` value; no default applies
 *                                      since the source query always selects it
 */
export interface PersonPrivacySettings {
  share_email: boolean;
  share_phone: boolean;
  share_address: boolean;
  share_birthdate: boolean;
  directory_opt_in: boolean;
}

export type MemberDirectoryContext = {
  asOf: Date;
  people: PersonWithHousehold[];
  /** Privacy settings keyed by person_id */
  privacyByPerson?: Record<string, PersonPrivacySettings>;
};

const buildPersonName = (person: PersonWithHousehold) => {
  if (person.entity_type === "company" && person.company_name) {
    const trimmed = person.company_name.trim();
    if (trimmed.length > 0) return trimmed;
  }
  // Trim each part so a whitespace-only `first_name` ("  ") doesn't survive
  // the `filter(Boolean)` and produce a label of " " (visually blank but
  // technically truthy).
  const parts = [person.first_name, person.last_name]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter((p) => p.length > 0);
  return parts.length > 0 ? parts.join(" ") : "Unknown";
};

/**
 * Format an address from a household record. Skips empty/whitespace
 * components and never produces dangling commas like ", , 12345".
 */
function buildAddress(household: PersonWithHousehold["households"]): string | null {
  if (!household) return null;
  const address = household.address?.trim();
  if (!address) return null;
  const city = household.city?.trim();
  const state = household.state?.trim();
  const zip = household.zip?.trim();
  const cityState = [city, state].filter((s) => s && s.length > 0).join(", ");
  const cityStateZip = [cityState, zip].filter((s) => s && s.length > 0).join(" ");
  const parts = [address, cityStateZip].filter((s) => s && s.length > 0);
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * Format a birthday as "Month Day" (no year, for privacy).
 *
 * Tolerates both bare `YYYY-MM-DD` strings AND full ISO-8601 timestamps
 * `YYYY-MM-DDTHH:MM:SSZ` — the previous implementation `.split("-")` then
 * coerced the third element directly to a Number, which gave NaN for
 * any ISO timestamp like "1990-05-09T00:00:00Z" and silently dropped the
 * birthday whenever upstream emitted a full timestamp.
 */
function formatBirthday(dob: string | null | undefined): string | null {
  if (typeof dob !== "string") return null;
  const trimmed = dob.trim();
  if (trimmed.length === 0) return null;
  // Match the leading `YYYY-MM-DD` regardless of whether a time component
  // follows. Local-calendar parse so a 1990-05-09 birthday doesn't shift
  // to 1990-05-08 in negative-offset timezones.
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, ys, ms, ds] = match;
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

/**
 * Format a status string for display: snake/kebab → Title Case, with whitespace
 * collapsed and trimmed. Same fix as PR #1550 (new-people-this-month) so the
 * directory doesn't render statuses like "Active  Member" with stray double
 * spaces.
 */
function formatStatus(status: string | null | undefined): string | null {
  if (typeof status !== "string") return null;
  const trimmed = status.trim();
  if (trimmed.length === 0) return null;
  return trimmed
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build a printable member directory.
 *
 * IMPORTANT: This report intentionally excludes all financial/giving data.
 * Giving information should only appear in giving-specific reports
 * (Contribution Detail, Year-End Statements, Giving by Fund, etc.) or
 * within individual person records with appropriate permissions.
 */
export function buildMemberDirectoryAggregation({
  asOf,
  people,
  privacyByPerson,
}: MemberDirectoryContext): ReportAggregation {
  // Defensive: if upstream payload is missing/null, render an empty directory
  // rather than TypeErroring on `.filter` / `.map`.
  const safePeople = Array.isArray(people) ? people : [];

  // Filter to people who have opted-in to directory.
  // - No entry in privacyByPerson (no row, or the row's is_listed is false)
  //   → exclude.
  // - Row exists with `directory_opt_in: false` → exclude.
  // - Row exists with any other value (`true`/null/undefined) → include.
  const directoryPeople = privacyByPerson
    ? safePeople.filter((p) => {
        const priv = privacyByPerson[p.id];
        return Boolean(priv) && priv.directory_opt_in !== false;
      })
    : safePeople;

  const rows = directoryPeople.map((person) => {
    const priv = privacyByPerson?.[person.id];
    // Per-field defaults are documented on `PersonPrivacySettings` above.
    const shareEmail = priv?.share_email ?? true;
    const sharePhone = priv?.share_phone ?? true;
    const shareAddress = priv?.share_address ?? true;
    const shareBirthday = priv?.share_birthdate ?? false;

    return {
      id: person.id,
      label: buildPersonName(person),
      email: shareEmail ? (person.email?.trim() || null) : null,
      phone: sharePhone ? (person.phone?.trim() || null) : null,
      address: shareAddress ? buildAddress(person.households) : null,
      // `birthdate` (canonical DOB column) lives on the `people` row;
      // `PersonWithHousehold` extends `Person` (only `title`/`job_title`/
      // `department`/etc. are omitted), so we read it directly without
      // an `as any` cast.
      birthday: shareBirthday ? formatBirthday(person.birthdate) : null,
      household: person.households?.name?.trim() || null,
      // Stash the household id so the unique-household summary metric can
      // count by id (households with the same name are NOT the same
      // household — "The Smith Family" can repeat across distinct rows).
      householdId: person.households?.id || null,
      status: formatStatus(person.status),
      entityType: "person" as const,
      drilldownUrl: `/people/${encodeURIComponent(person.id)}`,
    };
  });

  // Stable, locale-independent sort: alphabetic by last name (extracted from
  // the rendered label), then full label, then id as the final tiebreaker so
  // two unknown-named people don't reshuffle between refetches.
  rows.sort((a, b) => {
    const aLast = lastNameKey(a.label);
    const bLast = lastNameKey(b.label);
    if (aLast < bLast) return -1;
    if (aLast > bLast) return 1;
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

  // Summary KPIs.
  // - `totalPeople`: count of rows shown in the directory.
  // - `uniqueHouseholds`: count by `householdId` (NOT by household name —
  //   two distinct households can share a label like "The Smith Family"
  //   and the previous implementation would have collapsed them to a
  //   single count, under-reporting household count).
  const totalPeople = rows.length;
  const uniqueHouseholds = new Set(
    rows.map((r) => r.householdId).filter((id): id is string => Boolean(id)),
  ).size;

  return {
    asOf,
    summary: [
      buildReportSummaryItem("report.member_directory.people_count", totalPeople),
      { key: "households", label: "Households", value: uniqueHouseholds, format: "number" },
    ],
    rows,
  };
}

/**
 * Extract a last-name key for sorting. Uses the last whitespace-delimited
 * word of the rendered label so "John Smith" sorts under "S" and "The
 * Smith Family" also sorts under "F"-amily — pragmatic, locale-independent.
 * Falls back to the full label when there's no whitespace.
 */
function lastNameKey(label: string): string {
  const trimmed = label.trim();
  if (trimmed.length === 0) return "";
  const lastSpace = trimmed.lastIndexOf(" ");
  return (lastSpace === -1 ? trimmed : trimmed.slice(lastSpace + 1)).toLowerCase();
}
